import React, { useEffect, useState, useRef } from "react";
import {
  FaBars,
  FaSearch,
  FaBell,
  FaLink,
  FaUser,
  FaTasks,
  FaUniversity,
  FaFileAlt,
  FaComment,
  FaCog,
  FaGlobe,
  FaBook,
  FaMoneyBill,
  FaPassport,
  FaBriefcase,
  FaUserEdit,
  FaDollarSign,
  FaUserPlus,
  FaClipboardCheck,
  FaTimes,
} from "react-icons/fa";
import { CiCirclePlus } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleReminderModal,
  toggleSideBar,
  toggleToDoTaskModal,
} from "../../../state/reducers/applicationSlice";
import { clearUser } from "../../../state/reducers/userSlice";
import { getAdminNotification } from "../../../services/admin.services";
import { formatDistanceToNow } from "date-fns";
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../../services/user.services";
import logo from "../../../assets/logo.png";

const Header = () => {
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const user = useSelector((state) => state.user.userDetails);
  const user_id = useSelector((state) => state.user.user_id);
  const userRole = user?.role || "admin";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // States
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileNotifications, setShowMobileNotifications] = useState(false);
  const [showMobileProfile, setShowMobileProfile] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Refs
  const notificationRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const searchResultsRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileNotificationRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const mobileSearchRef = useRef(null);

  // Toggle functions
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showDropdown) setShowDropdown(false);
    if (showSearchResults) setShowSearchResults(false);
    if (showMobileMenu) setShowMobileMenu(false);
    if (showMobileProfile) setShowMobileProfile(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  const capitalizeFullName = (fullName = "") => {
    return fullName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (showNotifications) setShowNotifications(false);
    if (showSearchResults) setShowSearchResults(false);
    if (showMobileMenu) setShowMobileMenu(false);
    if (showMobileNotifications) setShowMobileNotifications(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    if (showNotifications) setShowNotifications(false);
    if (showDropdown) setShowDropdown(false);
    if (showSearchResults) setShowSearchResults(false);
    if (showMobileNotifications) setShowMobileNotifications(false);
    if (showMobileProfile) setShowMobileProfile(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  const toggleMobileNotifications = () => {
    setShowMobileNotifications(!showMobileNotifications);
    if (showMobileProfile) setShowMobileProfile(false);
    if (showMobileMenu) setShowMobileMenu(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  const toggleMobileProfile = () => {
    setShowMobileProfile(!showMobileProfile);
    if (showMobileNotifications) setShowMobileNotifications(false);
    if (showMobileMenu) setShowMobileMenu(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showMobileNotifications) setShowMobileNotifications(false);
    if (showMobileProfile) setShowMobileProfile(false);
    if (showMobileMenu) setShowMobileMenu(false);
  };

  // Search handler with debounce
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (term.length >= 1) {
      searchTimerRef.current = setTimeout(() => {
        setShowSearchResults(true);
        // performSearch(term);
      }, 200);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };

  // Action handlers
  const handleOpenSideBar = () => {
    dispatch(toggleSideBar());
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(clearUser());
    navigate("/login");
  };

  const handleEditProfile = () => {
    if (userRole === "student") {
      navigate("/student/edit-profile");
    } else {
      navigate("/admin/edit-profile");
    }
    setShowMobileProfile(false);
    setShowDropdown(false);
  };

  const handleCreateTask = () => {
    dispatch(toggleToDoTaskModal(true));
    setShowMobileProfile(false);
    setShowMobileMenu(false);
  };

  const buildQueryOnFromNotification = (notification) => {
    const text = (
      notification.message ||
      notification.title ||
      notification.description ||
      ""
    ).toLowerCase();

    // more flexible: works even if there's a dot / extra text after room name
    const roomMatch = text.match(/in room\s+(.+?)(?:\.|$)/i);
    if (!roomMatch) return undefined;

    const roomName = roomMatch[1].trim().toLowerCase();
    return roomToQueryMap[roomName]; // roomToQueryMap keys are lowercase
  };

  const handleCreateReminder = () => {
    try {
      dispatch(toggleReminderModal(true));
      setShowMobileProfile(false);
      setShowMobileMenu(false);
    } catch (error) {
      console.error("Error creating reminder:", error);
    }
  };

  // Notification functions
  const getNotifications = async () => {
    try {
      let res;
      if (userRole === "admin" && user?.admin_id) {
        res = await getAdminNotification(user.admin_id);
      } else if (userRole === "superadmin" && user?.superadmin_id) {
        res = await getAdminNotification(user.superadmin_id);
      } else {
        return;
      }

      if (res?.data?.notifications) {
        const newNotifications = res.data.notifications;
        setNotifications((prev) => {
          const prevString = JSON.stringify(prev);
          const newString = JSON.stringify(newNotifications);
          return prevString !== newString ? newNotifications : prev;
        });
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, mark_as_read: true } : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user_id);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, mark_as_read: true }))
      );
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Helper functions for search
  const categorizeResults = (results) => {
    const categories = {};
    results.forEach((result) => {
      if (!categories[result.category]) {
        categories[result.category] = [];
      }
      categories[result.category].push(result);
    });
    return categories;
  };

  const getCategoryName = (category) => {
    const categoryMap = {
      main: "Main",
      profile: "Profile",
      "user-management": "User Management",
      "task-management": "Task Management",
      "lead-management": "Lead Management",
      applications: "Applications",
      communication: "Communication",
      reporting: "Reports",
      settings: "Settings",
      documents: "Documents",
      financial: "Financial Services",
      services: "Services",
    };

    return (
      categoryMap[category] ||
      category.charAt(0).toUpperCase() + category.slice(1)
    );
  };

  // Load notifications and recent searches on mount
  useEffect(() => {
    getNotifications();
    const savedSearches = localStorage.getItem(`${userRole}_recent_searches`);
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (e) {
        console.error("Error parsing saved searches:", e);
      }
    }
  }, [user, userRole]);

  // Clean up search timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let intervalId;
    if (user && userRole) {
      getNotifications();
      intervalId = setInterval(() => {
        getNotifications();
      }, 15000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user, userRole]);

  // Handle click outside for all dropdown menus
  useEffect(() => {
    function handleClickOutside(event) {
      // Handle notification panel
      if (
        showNotifications &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }

      // Handle profile dropdown
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }

      // Handle search results dropdown
      if (
        showSearchResults &&
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }

      // Handle mobile notification panel
      if (
        showMobileNotifications &&
        mobileNotificationRef.current &&
        !mobileNotificationRef.current.contains(event.target) &&
        !event.target.closest(".notification-bell-mobile")
      ) {
        setShowMobileNotifications(false);
      }

      // Handle mobile profile dropdown
      if (
        showMobileProfile &&
        mobileProfileRef.current &&
        !mobileProfileRef.current.contains(event.target) &&
        !event.target.closest(".profile-image-mobile")
      ) {
        setShowMobileProfile(false);
      }

      // Handle mobile menu dropdown
      if (
        showMobileMenu &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        event.target.className !== "mobile-menu-button" &&
        !event.target.closest(".mobile-menu-button")
      ) {
        setShowMobileMenu(false);
      }

      // Handle mobile search
      if (
        showMobileSearch &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target) &&
        !event.target.closest(".mobile-search-button")
      ) {
        setShowMobileSearch(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    showNotifications,
    showDropdown,
    showSearchResults,
    showMobileMenu,
    showMobileNotifications,
    showMobileProfile,
    showMobileSearch,
  ]);

  const roomToQueryMap = {
    "cv upload": "cv_upload",
    lor: "lor",
    sop: "sop",
    "ug only": "ug_only",
    "final documents upload": "final_documents_upload",
    "university application process": "university_application_process",
    "university specific sop": "university_specific_sop",
    "shortlisting of universities": "shortlisting_of_universities",
    "visa process": "visa_process",
    "new application created for": "shortlisting",
  };

  // Get appropriate icon for a result based on its icon name
  const getIconForResult = (iconName) => {
    switch (iconName) {
      case "user":
      case "user-shield":
        return <FaUser className="text-blue-600" />;
      case "user-graduate":
      case "user-edit":
        return <FaUserEdit className="text-blue-600" />;
      case "user-plus":
        return <FaUserPlus className="text-blue-600" />;
      case "tasks":
        return <FaTasks className="text-green-600" />;
      case "university":
        return <FaUniversity className="text-amber-600" />;
      case "passport":
        return <FaPassport className="text-red-600" />;
      case "clipboard-check":
        return <FaClipboardCheck className="text-green-600" />;
      case "file-alt":
      case "file-upload":
        return <FaFileAlt className="text-purple-600" />;
      case "comments":
        return <FaComment className="text-indigo-600" />;
      case "money-bill":
      case "money-check":
        return <FaMoneyBill className="text-green-600" />;
      case "dollar-sign":
        return <FaDollarSign className="text-green-600" />;
      case "cog":
      case "cogs":
        return <FaCog className="text-gray-600" />;
      case "globe":
        return <FaGlobe className="text-blue-600" />;
      case "book":
        return <FaBook className="text-yellow-600" />;
      case "briefcase":
        return <FaBriefcase className="text-gray-600" />;
      default:
        return <FaLink className="text-gray-600" />;
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0).toUpperCase() || "";
    const last = lastName?.charAt(0).toUpperCase() || "";
    return first + last || "U";
  };

  return (
    <header className="fixed top-0 w-full z-[100] bg-white/90 backdrop-blur-md border-b border-slate-200/70 shadow-sm transition-all duration-300 ease-in-out">
      {/* Main Header Container */}
      <div className="px-3 sm:px-4 lg:px-8">
        {/* Mobile View (xs to md) */}
        <div className="flex items-center justify-between h-14 sm:h-16 md:hidden">
          {/* Left: Hamburger Menu */}
          <button
            className="text-indigo-600 text-xl sm:text-2xl p-2 hover:bg-indigo-50 rounded-lg transition-colors"
            onClick={handleOpenSideBar}
            aria-label="Toggle menu"
          >
            <FaBars />
          </button>

          {/* Center: Logo */}
          <div className="flex items-center justify-center">
            <img src={logo} alt="Logo" className="w-36 p-4" />
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Search Button - Mobile Only */}
            {/* <button
              className="text-purple-500 text-lg sm:text-xl p-1 hover:bg-purple-50 rounded-md transition-colors mobile-search-button"
              onClick={toggleMobileSearch}
              aria-label="Search"
            >
              <FaSearch />
            </button> */}

            {/* Notification Bell */}
            <div className="relative">
              <button
                ref={notificationButtonRef}
                className="text-indigo-600 text-lg sm:text-xl p-2 hover:bg-indigo-50 rounded-lg transition-colors notification-bell-mobile"
                onClick={toggleMobileNotifications}
                aria-label="Notifications"
              >
                <FaBell />
                {Array.isArray(notifications) &&
                  notifications.length > 0 &&
                  notifications.some((n) => !n.mark_as_read) && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center font-bold">
                      {notifications.filter((n) => !n.mark_as_read).length}
                    </span>
                  )}
              </button>

              {/* Mobile Notifications Dropdown */}
              {showMobileNotifications && (
                <div
                  ref={mobileNotificationRef}
                  className="absolute right-0 top-10 w-80 sm:w-96 bg-white shadow-xl rounded-2xl z-50 border border-slate-200 max-h-96 overflow-hidden"
                >
                  <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-gray-800">
                      Notifications
                    </h3>
                    {Array.isArray(notifications) &&
                      notifications.length > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Mark all read
                        </button>
                      )}
                  </div>

                  <div className="overflow-y-auto max-h-80">
                    {Array.isArray(notifications) &&
                    notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${
                            !notification.mark_as_read ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            handleMarkAsRead(notification.id);

                            const message =
                              notification.message?.toLowerCase() || "";
                            const linkTo = notification.link_to;

                            // Case 1: Lead Reminder — extract ID and navigate with state
                            if (message.includes("lead reminder") && linkTo) {
                              const match = linkTo.match(/\/leads\/(\d+)/); // match number after /leads/
                              const leadId = match?.[1];

                              if (leadId) {
                                let trimmedLink =
                                  linkTo.split("/").slice(0, -2).join("/") +
                                  "/";
                                console.log(trimmedLink);
                                // Avoid double-admin: remove one if duplicated
                                if (trimmedLink.startsWith("admin/leads")) {
                                  trimmedLink = trimmedLink.replace(
                                    "admin/leads",
                                    "/admin/leads"
                                  );
                                }

                                navigate(trimmedLink, { state: { leadId } });
                              } else {
                                navigate(linkTo);
                              }
                              return;
                            }

                            // Case 2: General Reminder — navigate to dashboard with reminder state
                            if (message.includes("reminder")) {
                              navigate("/admin/dashboard", {
                                state: { reminder: notification },
                              });
                              return;
                            }

                            // Case 3: Fallback navigation with optional queryOn
                            // Case 3: Chat/application navigation with backend query_on
                            if (linkTo) {
                              const queryOn = notification.query_on; // ✅ from backend

                              navigate(linkTo, {
                                state: queryOn ? { queryOn } : undefined,
                              });

                              return;
                            }
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <FaBell className="text-indigo-500 text-sm" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {notification.title}
                              </p>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {notification.description}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                {formatDistanceToNow(
                                  new Date(notification.created_at),
                                  { addSuffix: true }
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        <FaBell className="mx-auto text-2xl mb-2 text-gray-300" />
                        <p className="text-sm">No notifications found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile Image */}
            <div className="relative">
              <button
                className="profile-image-mobile p-1.5 hover:bg-indigo-50 rounded-full transition-colors"
                onClick={toggleMobileProfile}
                aria-label="Profile"
              >
                {user.profile_picture_url ? (
                  <img
                    src={user.profile_picture_url}
                    alt="Profile"
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-medium text-xs sm:text-sm">
                      {getInitials(user.first_name, user.last_name)}
                    </span>
                  </div>
                )}
              </button>

              {/* Mobile Profile Dropdown */}
              {showMobileProfile && (
                <div
                  ref={mobileProfileRef}
                  className="absolute right-0 top-10 w-64 sm:w-72 bg-white shadow-xl rounded-2xl z-50 border border-slate-200 overflow-hidden"
                >
                  {/* Profile Header */}
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <div className="flex items-center space-x-3">
                      {user.profile_picture_url ? (
                        <img
                          src={user.profile_picture_url}
                          alt="Profile"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-indigo-600 font-medium text-lg">
                            {getInitials(user.first_name, user.last_name)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 font-semibold truncate">
                          {capitalizeFullName(
                            `${user.first_name || ""} ${
                              user.last_name || ""
                            }`.trim()
                          ) || "User"}
                        </p>
                        <p className="text-gray-500 text-sm truncate">
                          {user.email || ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Profile Menu Items */}
                  <div className="py-2">
                    <button
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition-colors"
                      onClick={handleEditProfile}
                    >
                      <FaUserEdit className="mr-3 text-gray-500" />
                      <span>Edit Profile</span>
                    </button>

                    {/* Admin-only options */}
                    {userRole !== "student" && (
                      <>
                        <button
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition-colors"
                          onClick={handleCreateTask}
                        >
                          <CiCirclePlus className="mr-3 text-gray-500 text-lg" />
                          <span>Add Task</span>
                        </button>
                        <button
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition-colors"
                          onClick={handleCreateReminder}
                        >
                          <CiCirclePlus className="mr-3 text-gray-500 text-lg" />
                          <span>Add Reminder</span>
                        </button>
                      </>
                    )}

                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <button
                        className="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                        onClick={handleLogout}
                      >
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop/Tablet View (md and up) */}
        <div className="hidden md:flex items-center justify-between h-20 lg:h-24">
          {/* Left Section */}
          <div className="flex items-center gap-3 lg:gap-4">
            <button
              className="text-indigo-600 text-xl lg:text-2xl cursor-pointer p-2 hover:bg-indigo-50 rounded-lg transition-colors"
              onClick={handleOpenSideBar}
              aria-label="Toggle sidebar"
            >
              <FaBars />
            </button>
            <div className="flex items-center justify-center">
              <img src={logo} alt="Logo" className="w-32 lg:w-36 h-auto" />
            </div>
          </div>

          {/* Middle Section - Search Bar */}

          {/* Right Section */}
          <div className="flex items-center space-x-4 lg:space-x-6">
            {/* Notifications */}
            <div className="relative">
              <button
                ref={notificationButtonRef}
                className="text-slate-500 hover:text-slate-700 text-xl lg:text-2xl p-2 hover:bg-slate-100 rounded-full transition-all"
                onClick={toggleNotifications}
                aria-label="Notifications"
              >
                <FaBell />
                {Array.isArray(notifications) &&
                  notifications.length > 0 &&
                  notifications.some((n) => !n.mark_as_read) && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 lg:w-6 lg:h-6 rounded-full flex items-center justify-center font-bold">
                      {notifications.filter((n) => !n.mark_as_read).length}
                    </span>
                  )}
              </button>

              {/* Desktop Notifications Dropdown */}
              {showNotifications && (
                <div
                  ref={notificationRef}
                  className="absolute right-0 top-12 w-80 lg:w-96 bg-white shadow-xl rounded-2xl border border-slate-200 z-50 overflow-hidden"
                >
                  <div className="flex justify-between items-center p-4 border-b border-slate-200 bg-slate-50">
                    <h2 className="text-lg font-semibold text-gray-800">
                      Notifications
                    </h2>
                    {Array.isArray(notifications) &&
                      notifications.length > 0 && (
                        <button
                          className="text-indigo-600 text-sm hover:text-indigo-800 font-medium"
                          onClick={handleMarkAllAsRead}
                        >
                          Mark all read
                        </button>
                      )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {Array.isArray(notifications) &&
                    notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors ${
                            notification.mark_as_read ? "" : "bg-blue-50"
                          }`}
                          onClick={() => {
                            handleMarkAsRead(notification.id);

                            const message =
                              notification.message?.toLowerCase() || "";
                            const linkTo = notification.link_to;

                            // Case 1: Lead Reminder — extract ID and navigate with state
                            if (message.includes("lead reminder") && linkTo) {
                              const match = linkTo.match(/\/leads\/(\d+)/); // match number after /leads/
                              const leadId = match?.[1];

                              if (leadId) {
                                let trimmedLink =
                                  linkTo.split("/").slice(0, -2).join("/") +
                                  "/";
                                console.log(trimmedLink);
                                // Avoid double-admin: remove one if duplicated
                                if (trimmedLink.startsWith("admin/leads")) {
                                  trimmedLink = trimmedLink.replace(
                                    "admin/leads",
                                    "/admin/leads"
                                  );
                                }

                                navigate(trimmedLink, { state: { leadId } });
                              } else {
                                navigate(linkTo);
                              }
                              return;
                            }

                            // Case 2: General Reminder — navigate to dashboard with reminder state
                            if (message.includes("reminder")) {
                              navigate("/admin/dashboard", {
                                state: { reminder: notification },
                              });
                              return;
                            }

                            // Case 3: Fallback navigation with optional queryOn
                            // if (linkTo) {
                            //   const roomMatch = message.match(/in room (.+)$/i);
                            //   const exactRoom = roomMatch?.[1]?.trim();
                            //   const query_on = roomToQueryMap[exactRoom];
                            //   console.log(
                            //     "Navigating to:",
                            //     linkTo,
                            //     "with query_on:",
                            //     query_on
                            //   );
                            //   if (!roomMatch) {
                            //     navigate(linkTo, {
                            //       state: query_on
                            //         ? { queryOn: query_on }
                            //         : undefined,
                            //     });
                            //   }
                            //   navigate(linkTo, {
                            //     state: query_on
                            //       ? { queryOn: query_on }
                            //       : undefined,
                            //   });
                            // }
                            // Case 3: Chat/application navigation with backend query_on
                            if (linkTo) {
                              const queryOn = notification.query_on; // ✅ from backend

                              navigate(linkTo, {
                                state: queryOn ? { queryOn } : undefined,
                              });

                              return;
                            }
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            {!notification.mark_as_read && (
                              <span className="h-3 w-3 bg-blue-600 rounded-full mt-1 flex-shrink-0"></span>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-500 mb-1">
                                {formatDistanceToNow(
                                  new Date(notification?.created_at),
                                  { addSuffix: true }
                                )}
                              </div>
                              <div className="text-sm text-gray-900 font-medium">
                                {notification.title || notification.message}
                              </div>
                              {notification.description && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {notification.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        <FaBell className="mx-auto text-3xl mb-3 text-gray-300" />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile Section */}
            <div
              ref={profileRef}
              className="relative flex items-center space-x-2 lg:space-x-3 cursor-pointer group"
            >
              {user.profile_picture_url ? (
                <img
                  src={user.profile_picture_url}
                  alt="Profile"
                  className="w-10 h-10 lg:w-12 lg:h-12 rounded-full object-cover ring-2 ring-transparent group-hover:ring-purple-200 transition-all"
                  onClick={toggleDropdown}
                />
              ) : (
                <div
                  className="w-10 h-10 lg:w-12 lg:h-12 bg-indigo-100 rounded-full flex items-center justify-center ring-2 ring-transparent group-hover:ring-indigo-200 transition-all"
                  onClick={toggleDropdown}
                >
                  <span className="text-indigo-600 font-semibold text-sm lg:text-base">
                    {getInitials(user.first_name, user.last_name)}
                  </span>
                </div>
              )}

              <div className="hidden lg:block" onClick={toggleDropdown}>
                <span className="text-gray-700 font-medium text-sm xl:text-base">
                  {capitalizeFullName(
                    `${user.first_name || ""} ${user.last_name || ""}`.trim()
                  ) || "User"}
                </span>
              </div>

              <button
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={toggleDropdown}
                aria-label="Toggle profile menu"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Desktop Profile Dropdown */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute top-12 right-0 bg-white shadow-xl rounded-lg z-50 border w-48 lg:w-56 overflow-hidden"
                >
                  <div className="py-2">
                    <button
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition-colors"
                      onClick={handleEditProfile}
                    >
                      <FaUserEdit className="mr-3 text-gray-500" />
                      <span>Edit Profile</span>
                    </button>

                    <div className="border-t border-gray-200 my-2"></div>

                    <button
                      className="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                      onClick={handleLogout}
                    >
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Admin Only */}
            {userRole !== "student" && (
              <div className="flex items-center space-x-3 lg:space-x-4">
                <button
                  className="hidden lg:flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-colors shadow-sm"
                  onClick={handleCreateTask}
                >
                  <CiCirclePlus className="mr-2 text-lg" />
                  <span className="text-sm">Add Task</span>
                </button>

                <button
                  className="hidden lg:flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-colors shadow-sm"
                  onClick={handleCreateReminder}
                >
                  <CiCirclePlus className="mr-2 text-lg" />
                  <span className="text-sm">Add Reminder</span>
                </button>

                {/* Compact buttons for medium screens */}
                <button
                  className="lg:hidden flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-sm"
                  onClick={handleCreateTask}
                  title="Add Task"
                >
                  <CiCirclePlus className="text-lg" />
                </button>

                <button
                  className="lg:hidden flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-sm"
                  onClick={handleCreateReminder}
                  title="Add Reminder"
                >
                  <CiCirclePlus className="text-lg" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
